import sanitizeHtml from "sanitize-html"
import { z } from "zod"

import type { UnlikelyProduct } from "@unlikelystudio/commerce-connector"

import { getLang } from "~/lib/i18n/utils/get-i18n"
import { sbRichtextSchema, type SbRichtext } from "~/lib/storyblok/schemas/default/richtext"
import { asRichText } from "~/components/ui/RichText/_data/mock"
import type { AbstractLinkProps } from "~/components/abstracts/Link"
import { fetchDictionary } from "~/providers/I18nProvider/_data/get"
import type { metafieldsToObject } from "~/utils/metafields-to-object"
import { objectEntries } from "~/utils/object-entries"

const productGlobalAccordions = ["delivery_and_return"]

export const TRACEABILITY_KEY = "traceability"

export const productGlobalSettingsSchema = z
  .object({
    delivery_and_return: z.array(z.object({ title: z.string(), text: sbRichtextSchema() })),
  })
  .nullable()

type TProductGlobalSettings = z.infer<typeof productGlobalSettingsSchema>

type TDescriptionAccordion = { description?: string; productRef?: string }

export type TProductAccordion =
  | { head: string; body: Partial<AbstractLinkProps> }
  | { head: string; body: TDescriptionAccordion }
  | { head: string; body: SbRichtext }

async function serializeProductAccordions(
  product: UnlikelyProduct,
  productGlobalSettings: TProductGlobalSettings,
  metafields: ReturnType<typeof metafieldsToObject>,
  locale: string
): Promise<{ items: TProductAccordion[] }> {
  const dictionary = await fetchDictionary(locale)

  /*  /!\  /!\  /!\
   *  For now:
   *  - English product description is stored in a metafield
   *  - French product description is stored in the product html description
   *  Hence, our conditional sheaninagans
   *  /!\  /!\  /!\  */
  const description =
    getLang(locale) === "fr" ? product?.descriptionHtml : metafields?.description ?? product?.descriptionHtml
  const descriptionAccordion = {
    head: String(dictionary?.product_description_title) ?? "",
    body: { description, productRef: metafields?.ref ?? null } as TDescriptionAccordion,
  }

  const compositionAccordion = metafields?.composition
    ? {
        head: String(dictionary?.product_composition_title) ?? "",
        body: asRichText({ type: "paragraph", texts: [sanitizeHtml(metafields?.composition, { allowedTags: [] })] }),
      }
    : null

  const careAccordion = metafields?.care
    ? {
        head: String(dictionary?.product_care_title) ?? "",
        body: asRichText({ type: "paragraph", texts: [sanitizeHtml(metafields.care, { allowedTags: [] })] }),
      }
    : null

  const traceabilityAccordion = metafields?.traceability
    ? {
        head: String(dictionary?.product_traceability_title) ?? "",
        isTraceability: true,
        body: {
          href: metafields?.traceability,
          /** /!\ DO NOT CHANGE THE CHILDREN PROPERTY AS IT WILL HELP IDENTITY THIS ENTRY **/
          children: TRACEABILITY_KEY,
          obfuscate: true,
        } as Partial<AbstractLinkProps>,
      }
    : null

  const sbAccordionItems = productGlobalSettings
    ? objectEntries(productGlobalSettings)
        .map(([key, _]) => (productGlobalAccordions.indexOf(key) !== -1 ? _ : null))
        .filter(Boolean)
    : null

  const serializedAccordionsItems =
    sbAccordionItems
      ?.map((item) => (item[0]?.title && item[0]?.text ? { head: item[0]?.title, body: item[0]?.text } : null))
      .filter(Boolean) ?? []

  return {
    items: [
      descriptionAccordion,
      careAccordion,
      compositionAccordion,
      ...(traceabilityAccordion ? [traceabilityAccordion] : []),
      ...serializedAccordionsItems,
    ].filter(Boolean),
  }
}

export { serializeProductAccordions }
